import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { PencilSquareIcon, ShieldCheckIcon, UserCircleIcon, KeyIcon } from '@heroicons/react/24/outline';
import { Loader2, ShieldAlert, Check, X, Flag, ShieldOff } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  useAdminUsersList,
  useAdminRolesList,
  useRoleCounts,
  useUserAnalytics,
  useAdminOrganizations,
  useToggleMembershipActive,
  useImpersonateUser,
  type AdminUser,
  type AdminUsersFilters,
  type UserAnalyticsFilters,
} from '../api/extensions/useAdminOps';
import { UserAnalyticsCards } from '../components/admin/UserAnalyticsCards';
import { authService } from '../services/auth.service';
import { resetClientState } from '../lib/resetClientState';
import {
  usePendingMembershipClaims,
  useApproveMembershipClaim,
  useRejectMembershipClaim,
  type PendingMembershipClaim,
} from '../api/extensions/useMembershipVerificationOps';
import { useToast } from '../contexts/ToastContext';
import { useOrganizationsList } from '../api/generated_hooks/generated-organizations-hooks';
import { useAuthStore } from '../state_management_and_stores/auth.store';
import EditUserModal from '../components/EditUserModal';
import AdminResetPasswordModal from '../components/AdminResetPasswordModal';

function calculateAge(dob: string | undefined | null): number | null {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}
import UserManagementModal from '../components/UserManagementModal';
import { StatusBadge } from '../components/ui/status-badge';
import { SectionLabel } from '../components/ui/section-label';
import { Pagination } from '../components/Pagination';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { highlightSearchTerms, highlightPhoneDigits } from '../../../../config/search/search-utils';
import { getDisplayNameSupplement } from '../utils/adminNameDisplay';
import { notify } from '../utils/notifications';
import { tableConfig, getRowStyles, getHeaderStyles } from '../../../../config/ui/tables/table-config';
import '../../../../config/ui/tables/table-styles.css';

// Role chip colors — active state uses the role's signature color as background
const roleChipColors: Record<string, { active: string; ring: string }> = {
  admin:     { active: 'bg-red-600 text-white border-red-600',      ring: 'ring-red-300' },
  delegate:  { active: 'bg-purple-600 text-white border-purple-600', ring: 'ring-purple-300' },
  secretary: { active: 'bg-blue-600 text-white border-blue-600',    ring: 'ring-blue-300' },
  system:    { active: 'bg-gray-600 text-white border-gray-600',    ring: 'ring-gray-300' },
  member:    { active: 'bg-green-600 text-white border-green-600',  ring: 'ring-green-300' },
  user:      { active: 'bg-sky-600 text-white border-sky-600',      ring: 'ring-sky-300' },
};

const roleChipInactive: Record<string, string> = {
  admin:     'text-red-400 border-red-400/30 hover:bg-red-500/10',
  delegate:  'text-purple-400 border-purple-400/30 hover:bg-purple-500/10',
  secretary: 'text-blue-400 border-blue-400/30 hover:bg-blue-500/10',
  system:    'text-gray-400 border-gray-400/30 hover:bg-gray-500/10',
  member:    'text-green-400 border-green-400/30 hover:bg-green-500/10',
  user:      'text-sky-400 border-sky-400/30 hover:bg-sky-500/10',
};

const defaultChipActive = 'bg-blue-600 text-white border-blue-600';
const defaultChipInactive = 'text-gray-400 border-gray-400/30 hover:bg-gray-500/10';

function ManageUsersPage() {
  // --- State ---
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'email'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  // Deactivated-only chip. Off by default so the table hides soft-deactivated
  // users; clicking the chip switches the activationStatus filter to
  // 'deactivated' so only those accounts appear (the spec is "ONLY show
  // those accounts if that filter is selected").
  const [showDeactivatedOnly, setShowDeactivatedOnly] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);

  // Modals — store IDs, derive user from current query data (prevents stale snapshots)
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [managingUserId, setManagingUserId] = useState<string | null>(null);
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);

  // Auth — distinguish ALL-org admin (system-wide) from org-specific admin
  const isAllOrgAdmin = useAuthStore((s) => s.isAllOrgAdmin());
  const currentUserProfileId = useAuthStore((s) => s.profile?.id);
  const setAuthData = useAuthStore((s) => s.setAuthData);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Mutations
  const toggleActive = useToggleMembershipActive();
  const impersonateUser = useImpersonateUser();

  // Flagged-claims view: pending org-membership claims awaiting admin
  // approval. Driven via the same /membership-verification-ops endpoints
  // used by the dedicated MembershipVerificationPage; exposing them here
  // means admins land on the same Manage Users surface they already use.
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFlaggedOnly, setShowFlaggedOnly] = useState<boolean>(
    searchParams.get('flagged') === '1',
  );
  const [pendingClaimAction, setPendingClaimAction] = useState<{
    organization_id: string;
    user_profile_id: string;
    mode: 'approve' | 'reject';
  } | null>(null);
  const [claimRejectReason, setClaimRejectReason] = useState('');
  // Per-row admin overrides for the contestant's submitted member number,
  // keyed by `${org_id}:${user_profile_id}`. The field stays editable next
  // to every pending claim so admins can fix a typo without first clicking
  // Approve — without this they'd be forced to reject (which surcharges
  // every active registration the user has at the org and triggers a refund
  // cycle later) just to ask the user to re-submit. Falls back to the
  // claim's submitted number when no override has been typed yet.
  const [claimMemberNumberEdits, setClaimMemberNumberEdits] = useState<
    Record<string, string>
  >({});
  // Pending confirmation when an admin approves with a corrected number.
  // Keeps the unchanged-number path a single click while making the
  // corrected-number path explicit so an admin can't accidentally rewrite
  // someone's member number with a stray keystroke.
  const [pendingApprovalConfirm, setPendingApprovalConfirm] = useState<{
    target: PendingMembershipClaim;
    originalNumber: string;
    newNumber: string;
  } | null>(null);
  const { data: pendingClaimsData = [], isLoading: claimsLoading } =
    usePendingMembershipClaims();
  const approveClaim = useApproveMembershipClaim();
  const rejectClaim = useRejectMembershipClaim();
  const { showSuccess, showError } = useToast();

  // Keep ?flagged=1 in the URL in sync with the toggle so the notification
  // deep-link (and a refresh) lands the admin straight on this view.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (showFlaggedOnly) {
      next.set('flagged', '1');
    } else {
      next.delete('flagged');
    }
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFlaggedOnly]);

  const handleApproveClaim = async (target: PendingMembershipClaim) => {
    try {
      const key = `${target.organization_id}:${target.user_profile_id}`;
      const submitted = target.member_number?.trim() ?? '';
      // Fall back to the submitted value if the admin hasn't typed anything
      // — the field is pre-filled, so an untouched row should approve as-is.
      const edited = (claimMemberNumberEdits[key] ?? submitted).trim();
      // Only send the override when the admin actually changed the number.
      // Sending the same value would trigger a needless "we corrected your
      // number" notification on the backend.
      const overrideNumber = edited && edited !== submitted ? edited : undefined;

      const result = await approveClaim.mutateAsync({
        organizationId: target.organization_id,
        userProfileId: target.user_profile_id,
        memberNumber: overrideNumber,
      });
      const changed = (result as any)?.member_number_changed === true;
      const finalNumber = (result as any)?.member_number ?? submitted ?? null;
      showSuccess(
        'Membership approved',
        changed
          ? `${target.user_display_name ?? 'Contestant'} confirmed at ${
              target.organization_name ?? 'this org'
            } with corrected member #${finalNumber}.`
          : `${target.user_display_name ?? 'Contestant'} confirmed at ${
              target.organization_name ?? 'this org'
            }.`,
      );
      setPendingClaimAction(null);
      // Drop this row's edit so a future re-claim from the same user/org
      // pre-fills from the new submission instead of the prior override.
      setClaimMemberNumberEdits((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err: any) {
      showError('Failed to approve', err?.message ?? 'Unknown error');
    }
  };

  const handleRejectClaim = async (target: PendingMembershipClaim) => {
    try {
      const result = await rejectClaim.mutateAsync({
        organizationId: target.organization_id,
        userProfileId: target.user_profile_id,
        reason: claimRejectReason,
      });
      const surcharged = (result as any)?.surcharged_registrations ?? 0;
      showSuccess(
        'Membership claim rejected',
        surcharged > 0
          ? `${target.user_display_name ?? 'Contestant'} reverted to non-member; non-member fee added to ${surcharged} entr${surcharged === 1 ? 'y' : 'ies'}.`
          : `${target.user_display_name ?? 'Contestant'} reverted to non-member at ${target.organization_name ?? 'this org'}.`,
      );
      setPendingClaimAction(null);
      setClaimRejectReason('');
    } catch (err: any) {
      showError('Failed to reject', err?.message ?? 'Unknown error');
    }
  };

  // --- Debounce search ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Stable list of selected role UUIDs so the React Query key is referentially
  // equal across renders that don't actually change the selection — avoids a
  // refetch on every keystroke.
  const selectedRoleIdList = useMemo(
    () => Array.from(selectedRoleIds).sort(),
    [selectedRoleIds],
  );

  // --- Query filters ---
  // Multi-role filtering now runs on the SERVER as a UNION (any of) over
  // user_roles. The previous workaround (`pageSize: 5000` + client-side
  // `roles.some(...)`) silently dropped users beyond row 1000 because of
  // Supabase's 1000-row response cap.
  // Backend defaults activationStatus to 'active' when omitted (matches
  // the "off by default" UX); the Deactivated chip flips it to
  // 'deactivated' which shows ONLY those accounts.
  const activationStatus: 'active' | 'deactivated' = showDeactivatedOnly
    ? 'deactivated'
    : 'active';

  const filters: AdminUsersFilters = useMemo(
    () => ({
      page,
      pageSize,
      search: debouncedSearch || undefined,
      orgId: selectedOrgId || undefined,
      // Send roleIds when 2+ are selected; for the single-select case use
      // roleId so backend logs / counts stay readable.
      ...(selectedRoleIdList.length >= 2
        ? { roleIds: selectedRoleIdList }
        : selectedRoleIdList.length === 1
          ? { roleId: selectedRoleIdList[0] }
          : {}),
      sortBy,
      sortOrder,
      activationStatus,
    }),
    [page, pageSize, debouncedSearch, selectedOrgId, selectedRoleIdList, sortBy, sortOrder, activationStatus],
  );

  // Analytics filters — same shape as the list filters minus pagination/sort
  // so the cards aggregate over the FULL filtered set. Memoized off the same
  // upstream values as `filters` so the query key only changes when one of
  // the filter dimensions actually changes (avoids a refetch on a page or
  // sort flip).
  const analyticsFilters: UserAnalyticsFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      orgId: selectedOrgId || undefined,
      ...(selectedRoleIdList.length >= 2
        ? { roleIds: selectedRoleIdList }
        : selectedRoleIdList.length === 1
          ? { roleId: selectedRoleIdList[0] }
          : {}),
      activationStatus,
    }),
    [debouncedSearch, selectedOrgId, selectedRoleIdList, activationStatus],
  );

  // --- Data fetching ---
  const { data: usersData, isLoading, error, refetch } = useAdminUsersList(filters);
  const { data: rolesData } = useAdminRolesList();
  const { data: roleCountsData } = useRoleCounts(selectedOrgId || undefined, activationStatus);
  // Cheap parallel count query so the Deactivated chip can show "(N)"
  // alongside the chip label — same pattern as Flagged. We only need the
  // `total` field, so pageSize=1 keeps the response tiny. Scoped to the
  // selected org so the count tracks the same admin scope the rest of the
  // page uses; React Query caches by key, so paging through the table
  // doesn't refetch the count.
  const { data: deactivatedCountData } = useAdminUsersList({
    page: 1,
    pageSize: 1,
    orgId: selectedOrgId || undefined,
    activationStatus: 'deactivated',
  });
  const deactivatedCount = deactivatedCountData?.total ?? 0;
  const { data: userAnalyticsData, isLoading: analyticsLoading } =
    useUserAnalytics(analyticsFilters);
  const { data: orgsData } = useAdminOrganizations();
  // Fetch ALL organizations for system admins (admin-ops only returns admin's own orgs)
  const { data: allOrgsData } = useOrganizationsList();

  const rawUsers = usersData?.users ?? [];
  const serverTotal = usersData?.total ?? 0;
  const serverHasMore = usersData?.hasMore ?? false;
  const roles = rolesData?.roles ?? [];
  const roleCounts = roleCountsData?.counts ?? [];
  const adminOrgs = orgsData?.orgs ?? [];

  const ALL_ORG_ID = '00000000-0000-0000-0000-000000000001';

  // Orgs shown in the filter dropdown and passed to the management modal.
  // ALL-org admins see every real org (CRUD list is authoritative, admin-ops
  // is the fallback). Scoped admins only see the orgs they actually administer.
  const selectableOrgs = useMemo(() => {
    if (isAllOrgAdmin) {
      if (allOrgsData && Array.isArray(allOrgsData)) {
        return allOrgsData
          .filter((org: any) => org.id !== ALL_ORG_ID)
          .map((org: any) => ({
            id: org.id as string,
            name: org.name as string,
            short_name: org.short_name as string | undefined,
          }));
      }
      return adminOrgs.filter((org) => org.id !== ALL_ORG_ID);
    }
    return adminOrgs.filter((org) => org.id !== ALL_ORG_ID);
  }, [isAllOrgAdmin, allOrgsData, adminOrgs]);

  // Managed org IDs — undefined for ALL-org admins (unrestricted), array for scoped
  const managedOrgIds = useMemo(() => {
    if (isAllOrgAdmin) return undefined;
    return adminOrgs.map((org) => org.id).filter((id) => id !== ALL_ORG_ID);
  }, [isAllOrgAdmin, adminOrgs]);

  // Hide the dropdown when a scoped admin only manages one org — nothing to pick,
  // and the filters below should reflect that single org without extra UI.
  const hideOrgFilter = !isAllOrgAdmin && managedOrgIds !== undefined && managedOrgIds.length === 1;
  const pinnedOrgId = hideOrgFilter ? managedOrgIds![0] : undefined;

  useEffect(() => {
    if (pinnedOrgId && selectedOrgId !== pinnedOrgId) {
      setSelectedOrgId(pinnedOrgId);
    }
  }, [pinnedOrgId, selectedOrgId]);

  // Derive modal users from current query data (so they update after mutations)
  const editingUser = useMemo(() => {
    if (!editingUserId) return null;
    return rawUsers.find((u) => u.id === editingUserId) ?? null;
  }, [editingUserId, rawUsers]);

  const managingUser = useMemo(() => {
    if (!managingUserId) return null;
    return rawUsers.find((u) => u.id === managingUserId) ?? null;
  }, [managingUserId, rawUsers]);

  const resettingUser = useMemo(() => {
    if (!resettingUserId) return null;
    return rawUsers.find((u) => u.id === resettingUserId) ?? null;
  }, [resettingUserId, rawUsers]);

  // Server now does the multi-role UNION + pagination, so we just pass
  // through what the API returned. The old client-side filter is gone
  // because pageSize:5000 silently capped at 1000 for large orgs.
  const total = serverTotal;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasMore = serverHasMore;
  const users = rawUsers;

  // Role count lookup
  const roleCountMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const rc of roleCounts) {
      map.set(rc.role_name, rc.count);
    }
    return map;
  }, [roleCounts]);

  // --- Helpers ---
  const getInitials = (user: AdminUser): string => {
    const first = (user.first_name || '').charAt(0);
    const last = (user.last_name || '').charAt(0);
    return (first + last).toUpperCase() || 'U';
  };

  const getDisplayName = (user: AdminUser): string => {
    return (
      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
      user.primary_email ||
      'Unknown'
    );
  };

  // --- Handlers ---
  const handleRoleChipClick = useCallback(
    (roleId: string) => {
      setSelectedRoleIds((prev) => {
        const next = new Set(prev);
        if (next.has(roleId)) {
          next.delete(roleId);
        } else {
          next.add(roleId);
        }
        return next;
      });
      setPage(1);
    },
    [],
  );

  const handleOrgFilterChange = useCallback((orgId: string) => {
    setSelectedOrgId(orgId);
    setPage(1);
  }, []);

  const handleSort = useCallback((column: 'name' | 'created_at' | 'email') => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  }, [sortBy]);

  /**
   * Begin impersonating a user. Backend swaps the session_id cookie to the
   * target user's freshly-minted impersonation session; we then wipe the
   * React Query cache (so admin-scoped data doesn't leak through) and bounce
   * to /dashboard BEFORE flipping the auth store. Order matters: the admin
   * is currently sitting on /users (admin-only), and the moment the store
   * sees the impersonated user's "user" role, the route guard fires a
   * redirect to /unauthorized that wins the race against our own navigate.
   * Switching the URL first lets the new path render under the new identity
   * without an intermediate /unauthorized stop.
   */
  const handleImpersonate = async (user: AdminUser) => {
    try {
      const result = await impersonateUser.mutateAsync(user.id);
      resetClientState(queryClient);
      navigate('/dashboard', { replace: true });
      const session = await authService.validateSession();
      setAuthData({
        user: session.user,
        profile: session.profile,
        role: session.role,
        permissions: session.permissions,
        impersonator: session.impersonator ?? null,
      });
      notify.success(`Now impersonating ${result.targetName}`);
    } catch (err: any) {
      notify.error(err?.message || 'Failed to impersonate user');
    }
  };

  // --- Loading ---
  if (isLoading && !usersData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive/30 rounded-md p-4">
        <h3 className="text-destructive font-medium">Error loading users</h3>
        <p className="text-destructive/80 mt-1">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="mt-2 px-4 py-2 bg-destructive text-destructive-foreground rounded hover:bg-destructive/90"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header — title + count on the left, gender/age analytics on the
          right. The user count badge stacks beneath the title as a prominent
          stat (rather than an inline pill) so the headline number reads at the
          same weight as the analytics donuts. UserAnalyticsCards gets a
          leading divider so the title cluster is visually separated from the
          stats block, matching the dividers already used between the donuts. */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col items-center gap-2">
            <h1 className="text-2xl font-bold">Manage Users</h1>
            <span className="inline-flex items-center px-3 py-1 rounded-md text-xl font-bold tabular-nums bg-muted text-foreground">
              {total}
            </span>
          </div>
          {!showFlaggedOnly && (
            <UserAnalyticsCards
              data={userAnalyticsData}
              isLoading={analyticsLoading}
              leadingDivider
            />
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-muted-foreground"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-9"
                placeholder="Search by name, email, or phone..."
              />
            </div>
          </div>

          {/* Organization Filter — hidden for scoped admins who manage only one org */}
          {!hideOrgFilter && (
            <div className="min-w-[180px]">
              <Select value={selectedOrgId} onValueChange={(val) => handleOrgFilterChange(val === '__all__' ? '' : val)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={isAllOrgAdmin ? 'All Organizations' : 'All My Organizations'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{isAllOrgAdmin ? 'All Organizations' : 'All My Organizations'}</SelectItem>
                  {selectableOrgs.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {(org as any).short_name || org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Role chips + Flagged toggle — shared row so the Flagged badge
            stays inline (right-aligned via ml-auto) instead of wrapping to
            its own line. flex-wrap lets the Flagged group drop gracefully
            at narrow widths while still right-aligning in its own row.
            The Flagged toggle reveals the pending org-membership claims
            queue inline; the notification bell deep-links here via
            ?flagged=1, kept in sync by the useEffect above. */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {roles.map((role) => {
            const count = roleCountMap.get(role.role_name) ?? 0;
            const isActive = selectedRoleIds.has(role.id);
            const roleName = role.role_name.toLowerCase();
            const activeColors = roleChipColors[roleName]?.active ?? defaultChipActive;
            const inactiveColors = roleChipInactive[roleName] ?? defaultChipInactive;
            const ringColor = roleChipColors[roleName]?.ring ?? 'ring-blue-300';

            return (
              <button
                key={role.id}
                onClick={() => handleRoleChipClick(role.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  isActive
                    ? `${activeColors} ring-2 ${ringColor} shadow-sm`
                    : inactiveColors
                }`}
              >
                {role.role_name}
                <span
                  className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-xs font-semibold ${
                    isActive ? 'bg-white/25 text-white' : 'bg-muted text-muted-foreground opacity-70'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
          {selectedRoleIds.size > 0 && (
            <button
              onClick={() => {
                setSelectedRoleIds(new Set());
                setPage(1);
              }}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear filters
            </button>
          )}

          <div className="ml-auto flex items-center gap-2">
            {showFlaggedOnly && (
              <span className="text-xs text-muted-foreground">
                Showing users with pending member-number claims awaiting your review.
              </span>
            )}
            {showDeactivatedOnly && !showFlaggedOnly && (
              <span className="text-xs text-muted-foreground">
                Showing deactivated accounts only.
              </span>
            )}
            {/* Deactivated-only chip. Off by default — soft-deactivated
                accounts are hidden from the main list, the spec is explicit
                that they "should not be a part of the default selection".
                Mutually exclusive with the Flagged chip: enabling this
                disables Flagged (and vice-versa) since the two views are
                separate top-level surfaces — the underlying queries don't
                overlap. Page resets to 1 so the user doesn't land past the
                end of a smaller result set. */}
            <button
              type="button"
              onClick={() => {
                setShowDeactivatedOnly((v) => {
                  const next = !v;
                  if (next) setShowFlaggedOnly(false);
                  return next;
                });
                setPage(1);
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                showDeactivatedOnly
                  ? 'bg-red-600 text-white border-red-600 ring-2 ring-red-300 shadow-sm'
                  : 'text-red-400 border-red-400/30 hover:bg-red-500/10'
              }`}
              aria-pressed={showDeactivatedOnly}
              aria-label={
                showDeactivatedOnly
                  ? 'Show all users (currently filtered to deactivated)'
                  : 'Show only deactivated accounts'
              }
            >
              <ShieldOff className="h-3.5 w-3.5" />
              Deactivated
              <span
                className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-xs font-semibold ${
                  showDeactivatedOnly
                    ? 'bg-white/25 text-white'
                    : 'bg-muted text-muted-foreground opacity-70'
                }`}
              >
                {deactivatedCount}
              </span>
            </button>
            <button
              type="button"
              onClick={() =>
                setShowFlaggedOnly((v) => {
                  const next = !v;
                  // Mutually exclusive with Deactivated — flipping Flagged
                  // on clears the deactivated filter so we never present
                  // two competing views at once.
                  if (next) {
                    setShowDeactivatedOnly(false);
                    setPage(1);
                  }
                  return next;
                })
              }
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                showFlaggedOnly
                  ? 'bg-amber-600 text-white border-amber-600 ring-2 ring-amber-300 shadow-sm'
                  : 'text-amber-500 border-amber-500/30 hover:bg-amber-500/10'
              }`}
            >
              <Flag className="h-3.5 w-3.5" />
              Flagged
              <span
                className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-xs font-semibold ${
                  showFlaggedOnly
                    ? 'bg-white/25 text-white'
                    : 'bg-muted text-muted-foreground opacity-70'
                }`}
              >
                {pendingClaimsData.length}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Flagged claims view — pending org-membership claims with inline
          Approve/Reject. Rendered in place of the users table when the
          Flagged toggle is active so admins have a single surface for
          membership review. Reject backfills the non-member surcharge
          onto the user's existing registrations server-side. Amber
          border-2 + accent stripe matches the "this needs review"
          treatment used elsewhere in the app. */}
      {showFlaggedOnly && (
        <div className="relative rounded-lg border-2 border-amber-300 bg-card shadow-sm overflow-hidden dark:border-amber-900/60">
          <span
            aria-hidden
            className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500/70"
          />
          <div className="pl-5 pr-4 py-3 border-b">
            <SectionLabel
              icon={<ShieldAlert className="h-3.5 w-3.5" />}
              color="amber"
              trailing={
                <Badge variant="secondary">
                  {pendingClaimsData.length} pending
                </Badge>
              }
            >
              Pending member-number claims
            </SectionLabel>
          </div>
          {claimsLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : pendingClaimsData.length === 0 ? (
            <div className="p-12 text-center">
              <Flag className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">
                All caught up — no member-number claims to review.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {pendingClaimsData.map((target) => {
                const key = `${target.organization_id}:${target.user_profile_id}`;
                const submittedNumber = target.member_number ?? '';
                const editedNumber =
                  claimMemberNumberEdits[key] ?? submittedNumber;
                const trimmedEdited = editedNumber.trim();
                const trimmedSubmitted = submittedNumber.trim();
                const numberChanged =
                  trimmedEdited.length > 0 && trimmedEdited !== trimmedSubmitted;
                const isRejectMode =
                  pendingClaimAction?.organization_id === target.organization_id &&
                  pendingClaimAction?.user_profile_id === target.user_profile_id &&
                  pendingClaimAction.mode === 'reject';
                const submittedAt = target.joined_at
                  ? new Date(target.joined_at)
                  : null;
                const submittedAtValid =
                  submittedAt && !isNaN(submittedAt.getTime())
                    ? submittedAt
                    : null;
                const userDetailsHref = `/users/${target.user_profile_id}`;
                // Row-level navigation: clicking anywhere on the row that
                // isn't an interactive control (button/input/anchor) opens
                // the user details page. Reject-mode rows skip this since
                // they have their own inline form the admin is filling out.
                const handleRowActivate = () => {
                  if (isRejectMode) return;
                  navigate(userDetailsHref);
                };
                return (
                  <div
                    key={key}
                    className={`px-4 py-3 space-y-2 transition-colors ${
                      isRejectMode
                        ? ''
                        : 'cursor-pointer hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:bg-accent/40'
                    }`}
                    role={isRejectMode ? undefined : 'button'}
                    tabIndex={isRejectMode ? -1 : 0}
                    aria-label={
                      isRejectMode
                        ? undefined
                        : `Open details for ${target.user_display_name ?? 'this contestant'}`
                    }
                    onClick={(e) => {
                      if (isRejectMode) return;
                      // Defense-in-depth: don't navigate when the click
                      // originated inside an interactive control. The
                      // inner containers also stopPropagation, but this
                      // closest() check covers anything we missed and
                      // keeps middle/cmd-click on the inner <Link> from
                      // also firing this handler.
                      if (
                        (e.target as HTMLElement).closest(
                          'button, input, a, [data-stop-row-click]',
                        )
                      ) {
                        return;
                      }
                      handleRowActivate();
                    }}
                    onKeyDown={(e) => {
                      if (isRejectMode) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        // Don't intercept keyboard activation on inner
                        // controls (e.g. Space inside the input).
                        if (
                          (e.target as HTMLElement).closest(
                            'button, input, a, [data-stop-row-click]',
                          )
                        ) {
                          return;
                        }
                        e.preventDefault();
                        handleRowActivate();
                      }
                    }}
                  >
                    {/* Compact single-row layout: contestant on the left,
                        editable member-number input in the middle, action
                        buttons on the right. The "was X" change indicator
                        renders inline in the otherwise-empty gap so the row
                        height stays fixed when admins edit a number. */}
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Name is a real <Link> so middle-click and
                            cmd/ctrl-click open the user details page in a
                            new tab natively — onClick alone wouldn't get
                            those for free. */}
                        <Link
                          to={userDetailsHref}
                          className="font-medium truncate block hover:underline focus-visible:underline focus-visible:outline-none"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {target.user_display_name ?? 'Unknown contestant'}
                        </Link>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {[target.organization_name, target.user_email]
                            .filter(Boolean)
                            .join(' • ') || 'No org / email info'}
                        </div>
                        {/* Submission timestamp — the moment the contestant
                            submitted (or last re-submitted) this member-
                            number claim. Shown so admins can triage stale
                            queues at a glance. Falls back to an em-dash
                            when joined_at is missing (legacy rows) so we
                            don't render "Submitted Invalid Date". */}
                        <div
                          className="text-xs text-muted-foreground/80 mt-0.5"
                          title={
                            submittedAtValid
                              ? submittedAtValid.toLocaleString()
                              : undefined
                          }
                        >
                          Submitted{' '}
                          {submittedAtValid ? (
                            <>
                              {submittedAtValid.toLocaleDateString()}{' '}
                              <span className="text-muted-foreground/70">
                                {submittedAtValid.toLocaleTimeString(undefined, {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}
                              </span>
                            </>
                          ) : (
                            '—'
                          )}
                        </div>
                      </div>
                      {/* Inline "edited" indicator. Lives in the row's
                          existing horizontal flow so it doesn't push the
                          card taller — it sits in the gap between the
                          contestant info and the input. Hidden when the
                          number matches the contestant's submission. */}
                      {numberChanged && !isRejectMode && (
                        <span
                          className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 whitespace-nowrap"
                          title={`Original submission: ${trimmedSubmitted || '—'}`}
                        >
                          was{' '}
                          <span className="font-mono line-through opacity-80">
                            {trimmedSubmitted || '—'}
                          </span>
                        </span>
                      )}
                      {/* Always-editable member number — admins can fix a
                          typo here and click Approve in one step instead of
                          the reject → surcharge → refund cycle. */}
                      <div
                        className="flex items-center gap-2"
                        data-stop-row-click
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Label
                          htmlFor={`claim-member-number-${key}`}
                          className="text-xs text-muted-foreground whitespace-nowrap"
                        >
                          Member #
                        </Label>
                        <input
                          id={`claim-member-number-${key}`}
                          value={editedNumber}
                          onChange={(e) =>
                            setClaimMemberNumberEdits((prev) => ({
                              ...prev,
                              [key]: e.target.value,
                            }))
                          }
                          placeholder="Member number"
                          className="w-32 h-9 px-3 rounded-md border border-input bg-background text-sm font-mono"
                        />
                      </div>
                      <div
                        className="flex items-center gap-2"
                        data-stop-row-click
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isRejectMode ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setPendingClaimAction(null);
                                setClaimRejectReason('');
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={rejectClaim.isPending}
                              onClick={() => handleRejectClaim(target)}
                            >
                              {rejectClaim.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <X className="h-4 w-4 mr-2" />
                              )}
                              Confirm reject
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setPendingClaimAction({
                                  organization_id: target.organization_id,
                                  user_profile_id: target.user_profile_id,
                                  mode: 'reject',
                                })
                              }
                            >
                              <X className="h-4 w-4 mr-2" />
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              disabled={
                                approveClaim.isPending ||
                                trimmedEdited.length === 0
                              }
                              onClick={() => {
                                // Unchanged number → fire approval directly;
                                // changed number → confirm first so a stray
                                // keystroke can't silently rewrite someone's
                                // member number.
                                if (numberChanged) {
                                  setPendingApprovalConfirm({
                                    target,
                                    originalNumber: trimmedSubmitted,
                                    newNumber: trimmedEdited,
                                  });
                                } else {
                                  handleApproveClaim(target);
                                }
                              }}
                            >
                              {approveClaim.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4 mr-2" />
                              )}
                              Approve
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {isRejectMode && (
                      <div
                        className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3"
                        data-stop-row-click
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Label htmlFor={`claim-reject-reason-${key}`} className="text-xs">
                          Reason (sent to the contestant; optional)
                        </Label>
                        <input
                          id={`claim-reject-reason-${key}`}
                          value={claimRejectReason}
                          onChange={(e) => setClaimRejectReason(e.target.value)}
                          placeholder="e.g. We can't find that member number on file."
                          className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                        />
                        <p className="text-xs text-muted-foreground">
                          Rejecting deletes the membership row, reverts the
                          user to non-member, and adds the non-member fee to
                          every active registration they have at this org.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Users Table */}
      {!showFlaggedOnly && (
      <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="app-table app-table--interactive" style={tableConfig.baseStyle}>
            <thead>
              <tr>
                <th className="app-table-header" style={getHeaderStyles(false, false, false)}>
                  #
                </th>
                <th
                  className="app-table-header cursor-pointer select-none"
                  style={getHeaderStyles(false, false, false)}
                  onClick={() => handleSort('name')}
                >
                  <span className="inline-flex items-center gap-1">
                    User
                    <SortIndicator column="name" activeColumn={sortBy} order={sortOrder} />
                  </span>
                </th>
                <th className="app-table-header" style={getHeaderStyles(false, false, false)}>
                  Organizations & Roles
                </th>
                <th
                  className="app-table-header cursor-pointer select-none"
                  style={getHeaderStyles(false, false, false)}
                  onClick={() => handleSort('email')}
                >
                  <span className="inline-flex items-center gap-1">
                    Email
                    <SortIndicator column="email" activeColumn={sortBy} order={sortOrder} />
                  </span>
                </th>
                <th className="app-table-header" style={getHeaderStyles(false, false, false)}>
                  Phone
                </th>
                <th
                  className="app-table-header cursor-pointer select-none"
                  style={getHeaderStyles(false, false, false)}
                  onClick={() => handleSort('created_at')}
                >
                  <span className="inline-flex items-center gap-1">
                    Created
                    <SortIndicator column="created_at" activeColumn={sortBy} order={sortOrder} />
                  </span>
                </th>
                <th className="app-table-header" style={getHeaderStyles(false, false, false)}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="app-table-cell text-center py-8 text-muted-foreground"
                  >
                    {debouncedSearch || selectedOrgId || selectedRoleIds.size > 0
                      ? 'No users match the current filters'
                      : 'No users found'}
                  </td>
                </tr>
              ) : (
                users.map((user, index) => {
                  const displayName = getDisplayName(user);
                  // Opt-in display-name supplement: rendered under the legal
                  // name when present and different. Helper returns null when
                  // no supplement should be shown (empty or equal).
                  const displayNameSupplement = getDisplayNameSupplement(user);
                  return (
                    <tr
                      key={user.id}
                      className="app-table-row cursor-pointer"
                      style={getRowStyles(
                        hoveredRow === index,
                        selectedRow === index,
                        false,
                        index % 2 === 0,
                      )}
                      onMouseEnter={() => setHoveredRow(index)}
                      onMouseLeave={() => setHoveredRow(null)}
                      onClick={() => {
                        // Row click is the default action — open the user
                        // details page. Per-cell controls (action icons,
                        // org-toggle buttons) call e.stopPropagation() so
                        // they don't trigger this navigation.
                        setSelectedRow(index);
                        navigate(`/users/${user.id}`);
                      }}
                    >
                      {/* Row Number */}
                      <td className="app-table-cell text-sm tabular-nums text-muted-foreground">
                        {(page - 1) * pageSize + index + 1}
                      </td>

                      {/* User Column */}
                      <td className="app-table-cell">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full flex items-center justify-center bg-accent text-accent-foreground">
                              <span className="text-sm font-medium">
                                {getInitials(user)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-card-foreground">
                              {highlightSearchTerms(displayName, debouncedSearch)}
                              {(() => {
                                // Compact "(M - 29)" style — gender initial,
                                // then age. We render the parenthetical when
                                // either piece is known and just omit the
                                // separator if the other is missing.
                                const age = calculateAge(user.date_of_birth);
                                const genderInitial = user.gender
                                  ? String(user.gender).trim().charAt(0).toUpperCase()
                                  : '';
                                if (age == null && !genderInitial) return null;
                                const label = [genderInitial, age != null ? String(age) : '']
                                  .filter(Boolean)
                                  .join(' - ');
                                return (
                                  <span className="ml-1.5 text-xs text-muted-foreground font-normal">
                                    ({label})
                                  </span>
                                );
                              })()}
                              {user.is_active === false && (
                                <Badge
                                  variant="destructive"
                                  className="ml-2 align-middle text-[10px] uppercase tracking-wide"
                                >
                                  Deactivated
                                </Badge>
                              )}
                            </div>
                            {displayNameSupplement && (
                              <div
                                className="text-xs text-muted-foreground italic"
                                title="Display name (set by the user)"
                              >
                                aka {highlightSearchTerms(displayNameSupplement, debouncedSearch)}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Organizations & Roles Column */}
                      <td className="app-table-cell">
                        <OrgRolesCell user={user} managedOrgIds={managedOrgIds} onToggleActive={(userId, orgId, orgLabel, isActive) => {
                          toggleActive.mutate(
                            { userId, orgId },
                            {
                              onSuccess: () => notify.success(`${orgLabel} ${isActive ? 'deactivated' : 'activated'}`),
                              onError: (err: any) => notify.error(err?.message || 'Failed to toggle'),
                            },
                          );
                        }} isToggling={toggleActive.isPending} />
                      </td>

                      {/* Email Column */}
                      <td className="app-table-cell text-sm text-muted-foreground" title={user.primary_email || ''}>
                        {highlightSearchTerms(user.primary_email || '\u2014', debouncedSearch)}
                      </td>

                      {/* Phone Column \u2014 digit-only highlight that mirrors the
                          backend's formatting-agnostic phone filter, so a row
                          that matches a phone search always shows why. */}
                      <td
                        className="app-table-cell text-sm text-muted-foreground whitespace-nowrap"
                        title={user.phone || ''}
                      >
                        {user.phone
                          ? highlightPhoneDigits(user.phone, debouncedSearch)
                          : '\u2014'}
                      </td>

                      {/* Created Column \u2014 stacked date + time, matches the
                          "primary + secondary" treatment used elsewhere
                          (e.g. EntriesLedger "Entered" column). */}
                      <td className="app-table-cell text-sm text-muted-foreground whitespace-nowrap">
                        {user.created_at ? (
                          <>
                            <div>
                              {new Date(user.created_at).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-muted-foreground/70">
                              {new Date(user.created_at).toLocaleTimeString(undefined, {
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </div>
                          </>
                        ) : (
                          '\u2014'
                        )}
                      </td>

                      {/* Actions Column — icon buttons */}
                      <td className="app-table-cell text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingUserId(user.id);
                            }}
                            className="p-1.5 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
                            title="Edit profile"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setManagingUserId(user.id);
                            }}
                            className="p-1.5 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
                            title="Manage roles & organizations"
                          >
                            <ShieldCheckIcon className="h-5 w-5" />
                          </button>
                          {/* Reset password — sets a new password, confirms
                              the email if needed, and revokes their sessions.
                              Backend re-checks org scope on every call. */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setResettingUserId(user.id);
                            }}
                            className="p-1.5 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
                            title="Reset password / confirm email"
                          >
                            <KeyIcon className="h-5 w-5" />
                          </button>
                          {/* Impersonate — disabled on the admin's own row.
                              Backend re-validates org scope on every call,
                              so we don't try to mirror that complexity here;
                              the action just surfaces "you can't impersonate
                              yourself" via the standard error toast if a
                              user somehow targets it. */}
                          {user.id !== currentUserProfileId && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleImpersonate(user);
                              }}
                              disabled={impersonateUser.isPending}
                              className="p-1.5 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50"
                              title="Impersonate user (sign in as them to debug what they see)"
                            >
                              {impersonateUser.isPending && impersonateUser.variables === user.id ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                              ) : (
                                <UserCircleIcon className="h-5 w-5" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-border">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={pageSize}
            hasNextPage={hasMore}
            hasPrevPage={page > 1}
            onPageChange={(newPage) => {
              setPage(newPage);
              setSelectedRow(null);
            }}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
              setSelectedRow(null);
            }}
            variant="default"
            showPageSizeSelector={true}
            showItemCount={true}
            showJumpTo={false}
            className="justify-center sm:justify-between"
          />
        </div>
      </div>
      )}

      {/* Edit User Modal (profile fields only) — saves itself via the
          shared admin profile mutation, same as the inline editor on the
          user details page. */}
      <EditUserModal
        isOpen={!!editingUser}
        onClose={() => setEditingUserId(null)}
        user={editingUser}
      />

      {/* User Management Modal (org + roles) */}
      <UserManagementModal
        isOpen={!!managingUser}
        onClose={() => setManagingUserId(null)}
        user={managingUser}
        availableRoles={roles}
        availableOrgs={selectableOrgs}
        isSystemAdmin={isAllOrgAdmin}
        managedOrgIds={managedOrgIds}
        currentUserProfileId={currentUserProfileId}
      />

      {/* Admin password reset (also confirms email + revokes sessions) */}
      <AdminResetPasswordModal
        isOpen={!!resettingUser}
        onClose={() => setResettingUserId(null)}
        userId={resettingUser?.id ?? null}
        userName={resettingUser ? getDisplayName(resettingUser) : null}
        userEmail={resettingUser?.primary_email ?? null}
      />

      {/* Approve-with-correction confirmation. Only opens when the admin
          changed the member number — an exact-match approve is a single
          click. Guards against a stray keystroke silently rewriting a
          contestant's number. */}
      <Dialog
        open={!!pendingApprovalConfirm}
        onOpenChange={(open) => {
          if (!open) setPendingApprovalConfirm(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm member-number correction</DialogTitle>
            <DialogDescription>
              You are approving the following change for{' '}
              <span className="font-medium text-foreground">
                {pendingApprovalConfirm?.target.user_display_name ?? 'this contestant'}
              </span>
              {pendingApprovalConfirm?.target.organization_name ? (
                <>
                  {' '}at{' '}
                  <span className="font-medium text-foreground">
                    {pendingApprovalConfirm.target.organization_name}
                  </span>
                </>
              ) : null}
              . The contestant will be notified of the corrected number.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center gap-4 py-2">
            <div className="text-center">
              <div className="text-xs text-muted-foreground mb-1">
                Originally submitted
              </div>
              <div className="font-mono text-base px-3 py-2 rounded-md border bg-muted/50">
                {pendingApprovalConfirm?.originalNumber || '—'}
              </div>
            </div>
            <div className="text-muted-foreground">→</div>
            <div className="text-center">
              <div className="text-xs text-emerald-600 dark:text-emerald-400 mb-1">
                Corrected to
              </div>
              <div className="font-mono text-base px-3 py-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                {pendingApprovalConfirm?.newNumber || '—'}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingApprovalConfirm(null)}
              disabled={approveClaim.isPending}
            >
              Cancel
            </Button>
            <Button
              disabled={approveClaim.isPending}
              onClick={async () => {
                if (!pendingApprovalConfirm) return;
                const target = pendingApprovalConfirm.target;
                setPendingApprovalConfirm(null);
                await handleApproveClaim(target);
              }}
            >
              {approveClaim.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Confirm &amp; approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Sort direction indicator for table headers. */
function SortIndicator({
  column,
  activeColumn,
  order,
}: {
  column: string;
  activeColumn: string;
  order: 'asc' | 'desc';
}) {
  if (column !== activeColumn) {
    // Inactive — show muted up/down arrows
    return (
      <svg className="h-4 w-4 text-muted-foreground/40" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 3l4 5H4l4-5zm0 10l-4-5h8l-4 5z" />
      </svg>
    );
  }
  // Active — show single arrow
  return order === 'asc' ? (
    <svg className="h-4 w-4 text-foreground" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 4l4 5H4l4-5z" />
    </svg>
  ) : (
    <svg className="h-4 w-4 text-foreground" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 12l-4-5h8l-4 5z" />
    </svg>
  );
}

/**
 * Merged Organizations & Roles column.
 * Each org on its own row (sorted alphabetically), with the clickable org badge
 * and its role badges inline. Inactive orgs dim the roles too.
 * System-level roles (no org) shown last.
 */
function OrgRolesCell({
  user,
  onToggleActive,
  isToggling,
  managedOrgIds,
}: {
  user: AdminUser;
  onToggleActive: (userId: string, orgId: string, orgLabel: string, isActive: boolean) => void;
  isToggling: boolean;
  // undefined = unrestricted (ALL-org admin); array = scoped, show only these orgs
  managedOrgIds?: string[];
}) {
  const isScoped = managedOrgIds !== undefined;
  const managedSet = isScoped ? new Set(managedOrgIds) : null;

  // Build roles grouped by org — scoped admins skip roles outside their orgs
  // (including system-level roles with no org) since they don't manage those.
  const rolesByOrg = new Map<string | null, typeof user.roles>();
  for (const r of user.roles) {
    if (managedSet && (r.organization_id === null || !managedSet.has(r.organization_id))) {
      continue;
    }
    const key = r.organization_id;
    if (!rolesByOrg.has(key)) rolesByOrg.set(key, []);
    rolesByOrg.get(key)!.push(r);
  }

  // Sort org memberships alphabetically by display name. Scoped admins only
  // see memberships inside orgs they administer — a user may belong to many
  // orgs, but the viewer should only see the slice they actually manage.
  const sortedMemberships = [...user.organization_memberships]
    .filter((m) => !managedSet || managedSet.has(m.organization_id))
    .sort((a, b) => {
      const aName = (a.organization.short_name || a.organization.name).toLowerCase();
      const bName = (b.organization.short_name || b.organization.name).toLowerCase();
      return aName.localeCompare(bName);
    });

  // System-level roles (no org) are only meaningful to ALL-org admins.
  const systemRoles = isScoped ? [] : (rolesByOrg.get(null) || []);
  const hasAnyContent = sortedMemberships.length > 0 || systemRoles.length > 0;

  if (!hasAnyContent) {
    return <span className="text-sm text-muted-foreground/70">None</span>;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-1">
        {sortedMemberships.map((mem) => {
          const orgLabel = mem.organization.short_name || mem.organization.name;
          const orgRoles = rolesByOrg.get(mem.organization_id) || [];
          const inactive = !mem.is_active;

          return (
            <div key={mem.organization_id} className={`flex items-center gap-1.5 ${inactive ? 'opacity-50' : ''}`}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleActive(user.id, mem.organization_id, orgLabel, mem.is_active);
                    }}
                    disabled={isToggling}
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors shrink-0 ${
                      mem.is_active
                        ? 'bg-accent text-accent-foreground hover:bg-accent/80'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <span
                      className={`inline-block h-1.5 w-1.5 rounded-full ${
                        mem.is_active ? 'bg-green-500' : 'bg-red-500'
                      }`}
                    />
                    {orgLabel}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{mem.organization.name}</p>
                  <p className="text-primary-foreground/70">
                    Click to {mem.is_active ? 'deactivate' : 'activate'}
                  </p>
                </TooltipContent>
              </Tooltip>
              {orgRoles.map((r) => (
                <StatusBadge
                  key={r.role_id}
                  status={r.role.role_name}
                  variant="role"
                  className="text-xs"
                />
              ))}
            </div>
          );
        })}

        {/* System-level roles (no organization) */}
        {systemRoles.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted/50 text-muted-foreground shrink-0">
              System
            </span>
            {systemRoles.map((r) => (
              <StatusBadge
                key={r.role_id}
                status={r.role.role_name}
                variant="role"
                className="text-xs"
              />
            ))}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

export default ManageUsersPage;
